import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ao3AbsoluteUrl, ao3ResolveSeo, type Ao3Lang } from "@/seo/ao3SeoConfig";

const MANAGED = "data-ao3-seo";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute(MANAGED, "1");
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.setAttribute(MANAGED, "1");
}

export default function Ao3SeoManager() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang: Ao3Lang = (i18n.language || "en").startsWith("zh") ? "zh" : "en";

  useEffect(() => {
    const seo = ao3ResolveSeo(pathname, lang);
    const canonical = ao3AbsoluteUrl(seo.canonicalPath);

    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang === "zh" ? "zh-CN" : "en");
    }

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", seo.ogType);
    upsertMeta("property", "og:site_name", seo.ogSiteName);
    upsertMeta("property", "og:title", seo.ogTitle);
    upsertMeta("property", "og:description", seo.ogDescription);
    upsertMeta("property", "og:url", canonical);

    upsertMeta("name", "twitter:card", seo.twitterCard);
    upsertMeta("name", "twitter:title", seo.twitterTitle);
    upsertMeta("name", "twitter:description", seo.twitterDescription);

    if (seo.image) {
      upsertMeta("property", "og:image", seo.image);
      upsertMeta("name", "twitter:image", seo.image);
    }

    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (seo.noindex) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }
  }, [pathname, lang]);

  return null;
}